import React, { useState, useRef, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Plus, Upload, ChevronDown, Film, Camera, Image } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CreateEpisodeModal = ({ isOpen, onClose, showId, showName, userId, currentSeason }) => {
  const [episodeTitle, setEpisodeTitle] = useState('');
  const [episodeDescription, setEpisodeDescription] = useState('');
  const [episodeDuration, setEpisodeDuration] = useState(22); // Default to 22 minutes
  const [episodeImage, setEpisodeImage] = useState(null);
  const [episodeImagePreview, setEpisodeImagePreview] = useState('');
  const [episodeScenes, setEpisodeScenes] = useState([]);
  const [episodeScenesPreview, setEpisodeScenesPreview] = useState([]);
  const [creatingEpisode, setCreatingEpisode] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(currentSeason);
  const [showSeasonSelector, setShowSeasonSelector] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  
  // Set today's date as default
  const today = new Date();
  const formattedDate = today.toISOString().split('T')[0];
  const [date, setDate] = useState(formattedDate);
  
  const fileInputRef = useRef(null);
  const scenesInputRef = useRef(null);
  
  // Validate inputs when they change
  useEffect(() => {
    // Check title length
    if (episodeTitle.length > 26) {
      setTitleError(true);
    } else {
      setTitleError(false);
    }
    
    // Check description word count only if there is a description
    const wordCount = episodeDescription.trim().split(/\s+/).length;
    if (episodeDescription.trim() !== '' && wordCount > 26) {
      setDescriptionError(true);
    } else {
      setDescriptionError(false);
    }
  }, [episodeTitle, episodeDescription]);
  
  if (!isOpen) return null;
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setEpisodeImage(file);
      setEpisodeImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleImageClick = () => {
    fileInputRef.current.click();
  };
  
  const toggleDuration = () => {
    setEpisodeDuration(episodeDuration === 22 ? 44 : 22);
  };
  
  const handleSceneImageChange = (e) => {
    if (e.target.files.length > 0) {
      const newScenes = [...episodeScenes];
      const newPreviews = [...episodeScenesPreview];
      
      // Calculate how many more scenes we can add
      const remainingSlots = 6 - newScenes.length;
      
      if (remainingSlots <= 0) {
        // Already at max scenes, don't add more
        return;
      }
      
      // Only add up to the remaining slots
      Array.from(e.target.files).slice(0, remainingSlots).forEach(file => {
        newScenes.push(file);
        newPreviews.push(URL.createObjectURL(file));
      });
      
      setEpisodeScenes(newScenes);
      setEpisodeScenesPreview(newPreviews);
    }
  };
  
  const removeScene = (index) => {
    const newScenes = [...episodeScenes];
    const newPreviews = [...episodeScenesPreview];
    
    newScenes.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setEpisodeScenes(newScenes);
    setEpisodeScenesPreview(newPreviews);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require title, description, and cover image
    if (!episodeTitle.trim() || !episodeDescription.trim() || !episodeImage || titleError || descriptionError) {
      return;
    }
    
    try {
      setCreatingEpisode(true);
      setError(null);
      
      // Upload episode cover image
      let episodeImageUrl = '';
      const imageRef = ref(storage, `shows/${showId}/episodes/${Date.now()}_cover`);
      await uploadBytes(imageRef, episodeImage);
      episodeImageUrl = await getDownloadURL(imageRef);
      
      // Upload scene images if provided
      const sceneUrls = [];
      for (let i = 0; i < episodeScenes.length; i++) {
        const sceneRef = ref(storage, `shows/${showId}/episodes/${Date.now()}_scene_${i}`);
        await uploadBytes(sceneRef, episodeScenes[i]);
        const sceneUrl = await getDownloadURL(sceneRef);
        sceneUrls.push(sceneUrl);
      }
      
      // Create episode document
      const episodesRef = collection(db, 'shows', showId, 'episodes');
      
      // Convert date string to timestamp
      const episodeDate = date ? new Date(date) : today;
      
      const episodeData = {
        episodeTitle,
        episodeDescription,
        episodeDuration,
        episodeImage: episodeImageUrl,
        episodeScenes: sceneUrls,
        episodeSeason: selectedSeason,
        episodeCreatorId: userId,
        episodeDate: episodeDate,
        episodeCreationDate: serverTimestamp(),
        episodeEarnedScore: 0,
        episodePossibleScore: 0,
        episodeNumber: 0, // This will be updated by the show
      };
      
      const episodeDoc = await addDoc(episodesRef, episodeData);
      
      // Update the show with the new episode count
      const showRef = doc(db, 'shows', showId);
      await updateDoc(showRef, {
        lastUpdated: serverTimestamp()
      });
      
      // Call the calculateStreak function
      try {
        const functions = getFunctions();
        const calculateStreak = httpsCallable(functions, 'calculateStreak');
        
        await calculateStreak({
          showId,
          latestEpisodeDate: episodeDate.getTime()
        });
        
        console.log('Successfully calculated streak');
      } catch (error) {
        console.error('Error calculating streak:', error);
      }
      
      // Close the modal and reset form
      onClose();
      
    } catch (err) {
      console.error('Error creating episode:', err);
      setError('Failed to create episode. Please try again.');
    } finally {
      setCreatingEpisode(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      zIndex: 1100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem'
    }}>
      <div style={{
        padding: '1.5rem',
        backgroundColor: '#6600cc',
        borderRadius: '0.75rem',
        maxWidth: '45rem',
        width: '90%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            New Episode
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: '#999',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'flex-start'
        }}>
          {/* Left side - Image upload and scenes */}
          <div style={{
            width: '15rem', // Reduced from 40%
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {/* Cover image upload */}
            <div style={{
              position: 'relative'
            }}>
              <div 
                onClick={handleImageClick}
                style={{
                  aspectRatio: '3/2',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '.8rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed #444',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {episodeImagePreview ? (
                  <img 
                    src={episodeImagePreview} 
                    alt="Episode preview" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <>
                    <Image size={44} color="#666" />
                  
                  </>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{ display: 'none' }}
                accept="image/*"
              />
              
              {/* Episode number badge */}
              <div 
                onClick={() => setShowSeasonSelector(!showSeasonSelector)}
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  left: '0.5rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Season {selectedSeason} <ChevronDown size={14} />
              </div>
              
              {showSeasonSelector && (
                <div style={{
                  position: 'absolute',
                  top: '2rem',
                  left: '0.5rem',
                  marginTop: '0.25rem',
                  backgroundColor: '#333',
                  borderRadius: '0.25rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  zIndex: 20
                }}>
                  {[...Array(currentSeason)].map((_, i) => (
                    <div 
                      key={i}
                      onClick={() => {
                        setSelectedSeason(i + 1);
                        setShowSeasonSelector(false);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        backgroundColor: selectedSeason === i + 1 ? '#6600CC' : 'transparent',
                        color: 'white',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Season {i + 1}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Scenes input section - moved directly below cover */}
            <div style={{
              marginTop: '0rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                
                marginBottom: '1.2rem',
              }}>
                <span style={{ 
                  color: 'grey', 
                  fontSize: '0.9rem',
                  fontWeight: '500' 
                }}>
                  Scenes (optional)
                </span>
                
                <Film size={16} style={{marginLeft: '0.5rem'}} color="grey" />
                
                {episodeScenes.length > 0 && (
                  <span style={{ 
                    marginLeft: '0.5rem', 
                    color: '#999',
                    fontSize: '0.8rem'
                  }}>
                    {episodeScenes.length}/6
                  </span>
                )}
              </div>
              
              {/* Always show the scene grid with 6 slots */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gridTemplateRows: 'repeat(2, 1fr)',
                gap: '0.5rem'
              }}>
                {/* Map existing scenes */}
                {episodeScenesPreview.map((preview, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      aspectRatio: '3/2',
                      borderRadius: '0.25rem',
                      overflow: 'hidden',
                      backgroundColor: '#222'
                    }}
                  >
                    <img
                      src={preview}
                      alt={`Scene ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      onClick={() => removeScene(index)}
                      style={{
                        position: 'absolute',
                        top: '0.25rem',
                        right: '0.25rem',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '1.5rem',
                        height: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                
                {/* Add empty slots up to 6 */}
                {[...Array(Math.max(0, 6 - episodeScenesPreview.length))].map((_, index) => (
                  <div
                    key={`empty-${index}`}
                    onClick={() => episodeScenes.length < 6 && scenesInputRef.current.click()}
                    style={{
                      aspectRatio: '3/2',
                      
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '0.45rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: episodeScenes.length < 6 ? 'pointer' : 'not-allowed',
                      border: '2px dashed #444',
                      opacity: episodeScenes.length < 6 ? 1 : 0.5
                    }}
                  >
                    <Plus size={20} color="#666" />
                  
                  </div>
                ))}
              </div>
            </div>

            <input
              ref={scenesInputRef}
              id="scenesInput"
              type="file"
              accept="image/*"
              multiple
              onChange={handleSceneImageChange}
              style={{ display: 'none' }}
            />
          </div>
          
          {/* Right side - Episode details form */}
          <div style={{
      
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            marginLeft: 'auto',
            marginRight: '2rem'
          }}>
            {/* Title input - no label */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={episodeTitle}
                onChange={(e) => setEpisodeTitle(e.target.value)}
                placeholder="Episode Title"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontWeight: '600',
                  backgroundColor: 'rgb(0,0,0,.6)',
                  border: titleError ? '1px solid #E50914' : 'none',
                  borderRadius: '0.6rem',
                  color: 'white',
                  fontSize: '1.4rem'
                }}
              />
              <div style={{ 
                position: 'absolute', 
                right: '-.8rem', 
                bottom: '.2rem', 
                fontSize: '0.7rem', 
                color: titleError ? '#E50914' : '#999' 
              }}>
                {episodeTitle.length}/26
              </div>
            </div>
            
            {/* Duration and Date row */}
            <div style={{
              display: 'flex',
              gap: '1rem'
            }}>
              <div 
                onClick={toggleDuration}
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  height: '2rem', 
                  borderRadius: '.6rem',
                  padding: '.5rem',
                  background: 'rgb(0,0,0,.6)'
                }}
              >
                <label style={{fontWeight: '600', marginTop: '5px', marginLeft: '.2rem', color: 'grey'}}>Duration: </label>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: ' .2rem 0.75rem',
                    backgroundColor: episodeDuration === 22 ? '#6600CC' : '#333',
                    color: 'white',
                    height: '2rem',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontWeight: episodeDuration === 22 ? 'bold' : 'normal'
                  }}
                >
                  22m
                </button>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: ' .2rem 0.75rem',
                    height: '2rem',
                    backgroundColor: episodeDuration === 44 ? '#6600CC' : '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontWeight: episodeDuration === 44 ? 'bold' : 'normal'
                  }}
                >
                  44m
                </button>
              </div>
              
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  height: '2rem',
                  backgroundColor: 'rgb(0,0,0,.6)',
                  border: 'none',
                  borderRadius: '0.25rem',
                  color: 'white',
                  fontSize: '1rem'
                }}
              />
            </div>
            
            {/* Description - no label */}
            <div style={{ position: 'relative' }}>
              <textarea
                value={episodeDescription}
                onChange={(e) => setEpisodeDescription(e.target.value)}
                placeholder="Episode Description"
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  border: descriptionError ? '1px solid #E50914' : 'none',
                  borderRadius: '0.6rem',
                  color: 'white',
                  fontSize: '1rem',
                  resize: 'vertical',
                  paddingBottom: '2rem' // Make room for the counter
                }}
              />
              <div style={{ 
                position: 'absolute', 
                right: '-.8rem', 
                bottom: '.6rem', 
                fontSize: '0.7rem', 
                color: descriptionError ? '#E50914' : '#999' 
              }}>
                {episodeDescription.trim() ? episodeDescription.trim().split(/\s+/).length : 0}/26 words
              </div>
            </div>
            
            {/* Create button */}
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={handleSubmit}
                disabled={creatingEpisode || !episodeTitle.trim() || !episodeDescription.trim() || !episodeImage || titleError || descriptionError}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: (!episodeTitle.trim() || !episodeDescription.trim() || !episodeImage || titleError || descriptionError) 
                    ? 'rgba(200, 200, 200, 0.3)' 
                    : 'white',
                  color: (!episodeTitle.trim() || !episodeDescription.trim() || !episodeImage || titleError || descriptionError) 
                    ? '#999' 
                    : '#6600CC',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: (creatingEpisode || !episodeTitle.trim() || !episodeDescription.trim() || !episodeImage || titleError || descriptionError) 
                    ? 'not-allowed' 
                    : 'pointer',
                  opacity: creatingEpisode ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {creatingEpisode ? (
                  <>Creating...</>
                ) : (
                  <>
                    <Plus 
                      size={18} 
                      color={(!episodeTitle.trim() || !episodeDescription.trim() || !episodeImage || titleError || descriptionError) 
                        ? '#999' 
                        : '#6600CC'} 
                    />
                    Create Episode
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEpisodeModal; 